import { Component, Renderer2, Inject, OnInit, OnDestroy } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RelevantWhitepaperComponent } from '../relevant-whitepaper/relevant-whitepaper.component';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ResourcesService } from '../../../services/resources.service';
import { HttpClientModule } from '@angular/common/http';

@Component({
  selector: 'app-onlinemovie-ticket-booking',
  imports: [CommonModule, RelevantWhitepaperComponent, ReactiveFormsModule, HttpClientModule],
  standalone: true,
  providers: [ResourcesService],
  templateUrl: './onlinemovie-ticket-booking.component.html',
  styleUrl: './onlinemovie-ticket-booking.component.scss'
})
export class OnlinemovieTicketBookingComponent {
  isModalOpen: boolean = false;
  downloadForm: FormGroup;
  isSubmitting: boolean = false;
  submitSuccess: boolean = false;
  errorMessage: string = '';
  private jsonLdScript: any;

  // IMPORTANT: This string matches the Key added to the Dictionary in EmailService.cs
  readonly whitepaperTitle = "Whitepaper on Online Movie Ticket Booking";

  // Path for direct browser download (optional, if you want them to get it immediately + email)
  readonly pdfPath = "../../../../assets/resources/whitepaper/eseeticketbooking/OnlineMovieTicketBooking.pdf";

  constructor(
    private fb: FormBuilder,
    private resourcesService: ResourcesService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) {
    this.downloadForm = this.fb.group({
      name: ['', [Validators.required, Validators.pattern('^[A-Za-z]+( [A-Za-z]+)*$')]], // Added pattern for consistency with apply-position
      email: ['', [Validators.required, Validators.pattern('^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\\.[A-Za-z]{3,4}$')]]
    });
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }

  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }

  injectSchemaMarkup(): void {
    const movieBookingSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/white-paper/onlinemovie-ticket-booking",
          "url": "https://www.kalpitatechnologies.com/white-paper/onlinemovie-ticket-booking",
          "name": "Whitepaper on Online Movie Ticket Booking | Kalpita Technologies",
          "description": "A mobile-first online movie ticket booking application designed to simplify ticket purchasing through real-time availability, flexible booking and cancellation.",
          "isPartOf": {
            "@type": "WebSite",
            "@id": "https://www.kalpitatechnologies.com/#website",
            "name": "Kalpita Technologies",
            "url": "https://www.kalpitatechnologies.com/"
          }
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/white-paper/onlinemovie-ticket-booking#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/", "name": "Home" } },
            { "@type": "ListItem", "position": 2, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/white-paper", "name": "White Papers" } },
            { "@type": "ListItem", "position": 3, "item": { "@type": "WebPage", "@id": "https://www.kalpitatechnologies.com/white-paper/onlinemovie-ticket-booking", "name": "Online Movie Ticket Booking" } }
          ]
        },
        {
          "@type": "TechArticle",
          "headline": "Whitepaper on Online Movie Ticket Booking",
          "articleBody": "This whitepaper presents a mobile-first online movie ticket booking application designed to simplify ticket purchasing through real-time availability...",
          "author": { "@type": "Organization", "name": "Kalpita Technologies" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          }
        },
        {
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What makes this movie ticket booking platform different from existing solutions?",
              "acceptedAnswer": { "@type": "Answer", "text": "The platform differentiates itself through flexible ticket cancellation and rescheduling up to one hour before the show, rich theatre and movie insights, and QR code-based validation." }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": {
            "@type": "ImageObject",
            "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png"
          }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(movieBookingSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  openModal() {
    this.isModalOpen = true;
    this.submitSuccess = false;
    this.errorMessage = '';
    this.isSubmitting = false;
    this.downloadForm.reset();
  }

  closeModal() {
    this.isModalOpen = false;
  }

  onSubmit() {
    if (this.downloadForm.valid) {
      this.isSubmitting = true;
      this.errorMessage = '';

      // Construct Payload matching C# WhitepaperDownloadForm
      const payload = {
        Name: this.downloadForm.value.name,
        Email: this.downloadForm.value.email,
        WhitepaperTitle: this.whitepaperTitle
      };

      this.resourcesService.sendWhitepaperEmail(payload).subscribe({
        next: (response: any) => {
          // Check backend response structure. Usually returns { IsError: false, data: true }
          if (response && !response.IsError) {
            this.handleSuccess();
          } else {
            this.isSubmitting = false;
            this.errorMessage = response?.Message || 'Failed to send email. Please try again.';
          }
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Error sending email:', error);
          this.errorMessage = 'Server error. Please check your internet connection.';
        }
      });
    } else {
      this.downloadForm.markAllAsTouched();
    }
  }

  handleSuccess() {
    this.isSubmitting = false;
    this.submitSuccess = true;

    // OPTIONAL: Trigger automatic browser download alongside email
    // this.triggerFileDownload(); 

    // Close modal after 4 seconds
    setTimeout(() => {
      this.closeModal();
    }, 4000);
  }

  triggerFileDownload() {
    const link = document.createElement('a');
    link.href = this.pdfPath;
    link.download = 'rise-of-ai-agent.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
